import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Navigation,
  MessageCircle,
  Send,
  Car,
  Bus,
  Train
} from 'lucide-react';

const Contact = () => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha nome, email e mensagem.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    
    // Simular envio do formulário
    setTimeout(() => {
      toast({
        title: "Mensagem enviada!",
        description: "Recebemos sua mensagem e entraremos em contato em breve.",
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      setSubmitting(false);
    }, 1000);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Telefone',
      content: '(11) 99999-0000',
      action: 'tel:+5511999990000'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'contato@clinicaaymee.com',
      action: 'mailto:contato@clinicaaymee.com'
    },
    {
      icon: MapPin,
      title: 'Endereço',
      content: 'Rua das Flores, 123\nVila Madalena, São Paulo - SP\nCEP: 01234-567',
      action: 'https://maps.google.com/?q=Rua+das+Flores+123+Vila+Madalena+São+Paulo'
    },
    {
      icon: Clock,
      title: 'Horários',
      content: 'Segunda a Sexta: 8h às 18h\nSábado: 8h às 12h\nDomingo: Fechado',
      action: null
    }
  ];

  const transportOptions = [
    {
      icon: Car,
      title: 'De Carro',
      description: 'Estacionamento próprio disponível'
    },
    {
      icon: Bus,
      title: 'Ônibus',
      description: 'Linhas 702, 856, 875 - Ponto em frente'
    },
    {
      icon: Train,
      title: 'Metrô',
      description: 'Estação Vila Madalena (Linha 2-Verde) - 5 min caminhando'
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header da seção */}
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-primary border-primary">
            Contato e Localização
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Entre em contato
            <span className="block text-primary">conosco</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Estamos aqui para ajudar você. Entre em contato conosco através 
            dos canais abaixo ou visite nossa clínica.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Informações de contato */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-8">
              Informações de Contato
            </h3>
            
            <div className="space-y-6 mb-8">
              {contactInfo.map((info, index) => (
                <Card key={index} className="hover:shadow-medium transition-smooth">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <info.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-2">
                          {info.title}
                        </h4>
                        <div className="text-gray-600 whitespace-pre-line">
                          {info.content}
                        </div>
                        {info.action && (
                          <a 
                            href={info.action}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-smooth mt-2"
                          >
                            <Navigation className="h-4 w-4" />
                            {info.title === 'Endereço' ? 'Ver no mapa' : 'Entrar em contato'}
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Como chegar */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Navigation className="h-5 w-5" />
                  Como Chegar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {transportOptions.map((option, index) => (
                    <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                      <option.icon className="h-8 w-8 text-primary mx-auto mb-2" />
                      <h5 className="font-semibold text-gray-900 mb-1">
                        {option.title}
                      </h5>
                      <p className="text-sm text-gray-600">
                        {option.description}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* WhatsApp */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <MessageCircle className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      WhatsApp
                    </h4>
                    <p className="text-gray-600 mb-3">
                      Atendimento rápido e direto pelo WhatsApp
                    </p>
                    <Button 
                      className="bg-green-500 hover:bg-green-600 text-white"
                      onClick={() => window.open('https://wa.me/5511999990000', '_blank')}
                    >
                      Conversar no WhatsApp
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Formulário de contato */}
          <div>
            <Card className="shadow-medium">
              <CardHeader>
                <CardTitle className="text-2xl">
                  Envie uma Mensagem
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Nome *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Seu nome completo"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Telefone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="(11) 99999-9999"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="seu@email.com"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject">Assunto</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      placeholder="Assunto da sua mensagem"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Mensagem *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Escreva sua mensagem aqui..."
                      rows={5}
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full gradient-primary text-white"
                    disabled={submitting}
                  >
                    {submitting ? (
                      'Enviando...'
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Enviar Mensagem
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    Responderemos sua mensagem em até 24 horas.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Mapa */}
        <div className="mt-16">
          <Card className="overflow-hidden shadow-medium">
            <CardHeader>
              <CardTitle className="text-center">Nossa Localização</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="relative h-96 bg-gray-200">
                {/* Placeholder para o mapa - em produção, usar Google Maps ou similar */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="h-16 w-16 text-primary mx-auto mb-4" />
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">
                      AYMEE Clínica Multidisciplinar
                    </h4>
                    <p className="text-gray-600">
                      Rua das Flores, 123 - Vila Madalena<br />
                      São Paulo, SP - CEP: 01234-567
                    </p>
                    <Button 
                      className="mt-4 gradient-primary text-white"
                      onClick={() => window.open('https://maps.google.com/?q=Rua+das+Flores+123+Vila+Madalena+São+Paulo', '_blank')}
                    >
                      Abrir no Google Maps
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ rápido */}
        <div className="mt-16 bg-white rounded-2xl p-8 md:p-12 shadow-soft">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Perguntas Frequentes
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Vocês atendem convênios?
              </h4>
              <p className="text-gray-600">
                Sim, atendemos os principais convênios médicos. 
                Entre em contato para verificar se o seu está na nossa rede credenciada.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Como funciona o agendamento?
              </h4>
              <p className="text-gray-600">
                Você pode agendar online através do nosso site, por telefone 
                ou WhatsApp. Confirmamos o agendamento por email.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Posso cancelar ou remarcar?
              </h4>
              <p className="text-gray-600">
                Sim, pedimos apenas que nos avise com 24 horas de antecedência 
                para que possamos disponibilizar o horário para outros pacientes.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Vocês fazem atendimento online?
              </h4>
              <p className="text-gray-600">
                Oferecemos teleconsultas para algumas especialidades. 
                Consulte nossos profissionais sobre essa possibilidade.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;